/**
 * יצירה/עדכון הזמנת LDS לפני תשלום — אותו payload כמו VacationPackagesBooking.createCustomerPriceQuote.
 * לשימוש מצ'אט חבילות (מודאל תשלום) בלי לשכפל את מסך ההזמנה המלא.
 */
import {
  createLdsNewOrder,
  getLdsOrder,
  updateLdsOrder,
  findExtendedDeals,
} from "../services/fhpService";
import { get_depret_from_result, parseToLdsDate } from "./LDSHelpers";
import { prepareFlightsHotelPassengersInfoForLdsSave } from "./lds/prepareFlightsHotelPassengersInfoForLdsSave";
import { extendedDealRowsFromFindResponse } from "./lds/normalizeLdsExtendedDealRows";
import { getUserDetails } from "../services/authService";
import { buildLdsOrderPersistCommissionFields } from "./ldsOrderPersistCommission";
import { getOrCreatePublicLdsCheckoutIdempotencyKey } from "./publicLdsCheckoutIdempotency";
import {
  isPublicPriceRefreshRequiredError,
  publicPriceRefreshErrorFromResponse,
  resolvePublicCreateLdsSnapshotFields,
} from "./publicVacationPackageSnapshotId";
import { isLdsOrderResponseFailedForNavigation } from "./ldsOrderStatus";
import { resolveLdsErrorAction } from "./ldsSuccessNavigationGuard";
import {
  enrichPublicLdsOurPriceForPersist,
  publicLdsOrderPayableBodyFields,
} from "./stampPublicLdsOrderPayable";

export function resolveLdsDealIdFromSelectedResult(selectedResult) {
  if (!selectedResult) return null;
  if (Array.isArray(selectedResult.linkedFlights)) {
    return selectedResult.linkedFlights[0]?.ldsDealId ?? null;
  }
  return selectedResult.linkedFlights?.ldsDealId ?? null;
}

/** @returns {unknown[]|null} */
export async function fetchExtendedDealRowsForLdsDealId(ldsDealId) {
  if (ldsDealId == null || String(ldsDealId).trim() === "") return null;
  const res = await findExtendedDeals({ lds_deal_id: ldsDealId });
  const rows = extendedDealRowsFromFindResponse(res);
  return rows.length ? rows : null;
}

export function buildVacationPackageLdsOrderBody({
  selectedResult,
  roomsAndPaxInfo,
  ourPrice,
  ldsSupplement,
  extendedDealApiData,
  agentId,
  bookingChannel,
  later,
  payableTotalIls = null,
  publicFinalPriceIls = null,
  agentFinalPriceIls = null,
}) {
  const { depDate, retDate } = get_depret_from_result(selectedResult);
  const { dealId, linkedFlights, supplierDetails, hotelAllocation } = selectedResult;
  const supplierId = supplierDetails?.supplierId || "NONE";
  const user = getUserDetails();
  const persistComm = buildLdsOrderPersistCommissionFields(ourPrice);
  const querySiteId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("siteId")
      : null;
  const resolvedPublicSiteId =
    selectedResult?.agent_public_site_id ??
    selectedResult?.public_site_id ??
    selectedResult?.agentPublicSiteId ??
    selectedResult?.publicSiteId ??
    querySiteId ??
    null;
  const isPublicFlow =
    selectedResult?.is_public_site_order === true ||
    String(selectedResult?.order_source || "").trim().toLowerCase() === "public_site" ||
    resolvedPublicSiteId != null;
  if (isPublicFlow && !resolvedPublicSiteId) {
    throw new Error("PUBLIC_SITE_ID_REQUIRED_FOR_PUBLIC_LDS_ORDER");
  }

  const snapshotFields = isPublicFlow
    ? resolvePublicCreateLdsSnapshotFields(selectedResult)
    : {};
  const stampedOurPrice = isPublicFlow
    ? enrichPublicLdsOurPriceForPersist({
        ourPrice,
        publicBasePriceToAgentIls:
          selectedResult?.public_base_price_to_agent_ils ??
          selectedResult?.fullDeal?.public_base_price_to_agent_ils ??
          ourPrice?.public_base_price_to_agent_ils ??
          agentFinalPriceIls,
        publicSelectedRoomId:
          selectedResult?.public_selected_room_id ??
          selectedResult?.fullDeal?.public_selected_room_id,
        publicSelectedRoomSnapshot:
          selectedResult?.public_selected_room_snapshot ??
          selectedResult?.fullDeal?.public_selected_room_snapshot,
        selectedRoom: selectedResult?.selectedRoom,
        payableTotalIls,
        publicFinalPriceIls,
        agentFinalPriceIls,
      })
    : ourPrice;
  const payableBody = isPublicFlow
    ? publicLdsOrderPayableBodyFields({
        payableTotalIls,
        agentBaseIls:
          stampedOurPrice?.public_base_price_to_agent_ils ?? agentFinalPriceIls,
        markupIls: stampedOurPrice?.public_markup_ils,
      })
    : {};
  return {
    agent_id: agentId ?? user?.id,
    ...(bookingChannel ? { booking_channel: bookingChannel } : {}),
    ...(resolvedPublicSiteId ? { public_site_id: String(resolvedPublicSiteId) } : {}),
    ...(isPublicFlow ? { is_public_site_order: true } : {}),
    ...snapshotFields,
    ...(isPublicFlow
      ? {
          public_checkout_idempotency_key: getOrCreatePublicLdsCheckoutIdempotencyKey({
            siteId: resolvedPublicSiteId,
            dealId,
            depDate: parseToLdsDate(depDate),
            retDate: parseToLdsDate(retDate),
          }),
          ...payableBody,
        }
      : {}),
    later: later ? "true" : "false",
    commission: persistComm.commission,
    handling_fee: persistComm.handling_fee,
    currency: "USD",
    deal_id: dealId,
    deal_type: "FlightsHotel",
    supplierId,
    hotelAllocation: hotelAllocation ?? null,
    passengers_info: prepareFlightsHotelPassengersInfoForLdsSave(roomsAndPaxInfo, {
      linkedFlights: linkedFlights || null,
      selectedFlightOptionFromUser: selectedResult?.selectedFlightOptionFromUser ?? null,
      selectedResult,
      extendedDealApiData,
      context: "buildVacationPackageLdsOrderBody",
    }),
    ret_date: parseToLdsDate(retDate),
    dep_date: parseToLdsDate(depDate),
    supplement: ldsSupplement || [],
    ourPrice: stampedOurPrice,
    linkedFlights: linkedFlights || null,
    extended: extendedDealApiData || null,
  };
}

function assertNoLdsHardFailure(orderPayload, phase) {
  if (orderPayload && isLdsOrderResponseFailedForNavigation(orderPayload)) {
    const act = resolveLdsErrorAction({
      order: orderPayload,
      source: `ensureVacationPackageLdsOrderForPayment.${phase}`,
      paidContext: false,
    });
    const err = new Error(act.message);
    err.ldsErrorAction = act;
    throw err;
  }
}

function orderErrorMessage(res) {
  return (
    res?.data?.error ||
    res?.data?.exception ||
    res?.data?.message ||
    (res?.status ? `שגיאת שרת (${res.status})` : "שגיאה ביצירת הזמנה")
  );
}

/**
 * @param {{
 *   selectedResult: object,
 *   roomsAndPaxInfo: unknown,
 *   ourPrice: object,
 *   ldsSupplement?: object,
 *   extendedDealApiData: unknown,
 *   ourSavedLDSOrder?: { id?: string|number }|null,
 *   setOurSavedLDSOrder?: (o: object) => void,
 *   agentId?: string|number|null,
 *   bookingChannel?: string|null,
 *   later?: boolean,
 *   payableTotalIls?: number|null,
 *   publicFinalPriceIls?: number|null,
 *   agentFinalPriceIls?: number|null,
 * }} p
 * @returns {Promise<object>}
 */
export async function ensureVacationPackageLdsOrderForPayment(p) {
  const {
    selectedResult,
    roomsAndPaxInfo,
    ourPrice,
    ldsSupplement,
    extendedDealApiData,
    ourSavedLDSOrder,
    setOurSavedLDSOrder,
    agentId,
    bookingChannel,
    later = false,
    payableTotalIls = null,
    publicFinalPriceIls = null,
    agentFinalPriceIls = null,
  } = p;

  if (!selectedResult || !roomsAndPaxInfo) {
    throw new Error("חסרים פרטי חבילה או נוסעים — בחרו מחדש מהרשימה והשלימו טופס.");
  }

  const body = buildVacationPackageLdsOrderBody({
    selectedResult,
    roomsAndPaxInfo,
    ourPrice,
    ldsSupplement,
    extendedDealApiData,
    agentId,
    bookingChannel,
    later,
    payableTotalIls,
    publicFinalPriceIls,
    agentFinalPriceIls,
  });

  const persist = (data) => {
    if (typeof setOurSavedLDSOrder === "function" && data) {
      setOurSavedLDSOrder(data);
    }
    return data;
  };

  const existingOrderId = ourSavedLDSOrder?.id;
  if (existingOrderId) {
    const updateBody = { ...body, ourLdsId: existingOrderId };
    const res = await updateLdsOrder(updateBody);
    if (res?.status >= 400) {
      if (
        isPublicPriceRefreshRequiredError({
          response: res,
          data: res?.data,
          code: res?.data?.code,
        })
      ) {
        throw publicPriceRefreshErrorFromResponse(res);
      }
      throw new Error(orderErrorMessage(res));
    }
    const data = res?.data;
    if (!data) {
      throw new Error("שגיאה: לא התקבלו נתוני הזמנה מהשרת");
    }
    assertNoLdsHardFailure(data, "update");
    return persist(data);
  }

  const res = await createLdsNewOrder(body);
  if (res?.status && res.status >= 400) {
    if (
      isPublicPriceRefreshRequiredError({
        response: res,
        data: res?.data,
        code: res?.data?.code,
      })
    ) {
      throw publicPriceRefreshErrorFromResponse(res);
    }
    throw new Error(orderErrorMessage(res));
  }
  if (!res?.data) {
    throw new Error("שגיאה: לא התקבלה תשובה משרת ההזמנות");
  }

  if (res.data.id && Object.keys(res.data).length === 1) {
    try {
      const orderRes = await getLdsOrder({ id: res.data.id });
      if (orderRes?.data) {
        assertNoLdsHardFailure(orderRes.data, "fetch_after_create");
        return persist(orderRes.data);
      }
    } catch {
      /* fallback below */
    }
    const orderData = { ...res.data, status: "ok" };
    assertNoLdsHardFailure(orderData, "create_fallback_minimal");
    return persist(orderData);
  }

  assertNoLdsHardFailure(res.data, "create_full");
  return persist(res.data);
}
